// Ship to home: a completed bill sold in this store is sent to the customer by
// courier (markFashionShipToHome). The server then lists it with the online
// orders as channel "In store" (storeSale), and it is packed and shipped there
// like an online SHIP order. It can't be cancelled; it is returned from the bill.
// The rules mirror the server's FashionRules.validateShipTo / tenDigitMobile so
// the form says what is wrong before the call.

export const SHIP_TO_FIELDS = ["name", "mobileNo", "line1", "line2", "landmark", "city", "state", "pincode"];
export const MAX_SHIP_TO_TEXT = 200;

const text = (s) =>
  String(s ?? "")
    .replace(/[\r\n\t]+/g, " ")
    .trim()
    .slice(0, MAX_SHIP_TO_TEXT);

// A mobile number as ten digits: spaces, brackets, dashes and a leading
// +91 / 91 (12 digits) or 0 (11 digits) dropped. Null when it isn't one.
export function tenDigitMobile(mobile) {
  let d = String(mobile ?? "").replace(/[\s()+-]/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return /^[6-9]\d{9}$/.test(d) ? d : null;
}

// Form values → { address } ready for the mutation, or { error, field }.
export function validateShipTo(draft) {
  const d = draft || {};
  const name = text(d.name);
  if (!name) return { error: "Enter who the parcel is for.", field: "name" };
  const mobileNo = tenDigitMobile(d.mobileNo);
  if (!mobileNo) return { error: "Enter a 10-digit mobile number for the courier.", field: "mobileNo" };
  const line1 = text(d.line1);
  if (!line1) return { error: "Enter the address.", field: "line1" };
  const city = text(d.city);
  if (!city) return { error: "Enter the city.", field: "city" };
  const pincode = String(d.pincode ?? "").replace(/\s+/g, "");
  if (!/^[1-9]\d{5}$/.test(pincode)) return { error: "Enter a 6-digit PIN code.", field: "pincode" };
  const address = { name, mobileNo, line1, city, pincode };
  ["line2", "landmark", "state"].forEach((k) => {
    const v = text(d[k]);
    if (v) address[k] = v;
  });
  return { address };
}

// The form's starting values from the bill's customer, when there is one.
export function shipToDraft(customer) {
  const c = customer || {};
  return {
    name: c.name || "",
    mobileNo: c.mobileNo || "",
    line1: c.address1 || c.addressLine1 || "",
    line2: c.address2 || c.addressLine2 || "",
    landmark: "",
    city: c.city || "",
    state: c.state || c.region || "",
    pincode: c.pincode || c.zipCode || c.postalCode || "",
  };
}

// Values go as GraphQL variables, never pasted into the query text.
export const MARK_SHIP_TO_HOME_MUTATION =
  "mutation MarkFashionShipToHome($orderId: String!, $bunitId: String!, $address: inpFashionShipTo!) { markFashionShipToHome(orderId: $orderId, bunitId: $bunitId, address: $address) { status message recordId documentNo } }";

export const buildMarkShipToHomeMutation = (orderId, bunitId, address) => ({
  query: MARK_SHIP_TO_HOME_MUTATION,
  variables: { orderId: String(orderId ?? ""), bunitId: String(bunitId ?? ""), address },
});

// The address an "In store" order carries (shipTo, a JSON string) → an object,
// or null when it is missing or not readable.
export function parseShipTo(raw) {
  if (!raw) return null;
  let v = raw;
  if (typeof raw === "string") {
    try {
      v = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const out = {};
  SHIP_TO_FIELDS.forEach((k) => {
    if (v[k] != null && String(v[k]).trim()) out[k] = String(v[k]).trim();
  });
  return Object.keys(out).length ? out : null;
}

// One line for the list: "Asha Rao, 98450 12345 · 12 MG Road, Opp. Metro, Bengaluru, Karnataka 560001".
export function shipToLine(addr) {
  if (!addr) return "";
  const who = [addr.name, addr.mobileNo].filter(Boolean).join(", ");
  const place = [addr.line1, addr.line2, addr.landmark, addr.city, [addr.state, addr.pincode].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [who, place].filter(Boolean).join(" · ");
}
